import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { validateField, SEVERITIES, BROWSERS, OS_LIST } from "@/lib/validation";
import { AlertOctagon, Upload, ImageIcon, Terminal, Send } from "lucide-react";

const initial = {
  title: "", steps: "", expected: "", actual: "",
  severity: "", browser: "", browser_other: "", os: "", evidence_text: "", evidence_file_id: "",
};

const FIELD_LABELS = {
  title: "Título del bug",
  steps: "Pasos para reproducir",
  expected: "Resultado esperado",
  actual: "Resultado actual",
};

export default function NewBug() {
  const nav = useNavigate();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState("");

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const checkField = (name, value) => {
    const err = validateField(name, value);
    setErrors((e) => ({ ...e, [name]: err }));
    return !err;
  };

  const readyToSubmit = useMemo(() => {
    const browserOk = form.browser && (form.browser.toLowerCase() !== "otro" || form.browser_other.trim().length >= 5);
    return (
      form.title && form.steps && form.expected && form.actual &&
      form.severity && browserOk && form.os &&
      (form.evidence_file_id || form.evidence_text.trim().length >= 15)
    );
  }, [form]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setGlobalError("Solo imágenes permitidas para la evidencia.");
      return;
    }
    setFileName(f.name); setUploading(true); setGlobalError("");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("evidence_file_id", data.file_id);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } catch (err) {
      setGlobalError(formatApiError(err));
      setFileName("");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setGlobalError("");
    const newErrors = {};
    let ok = true;
    for (const k of ["title", "steps", "expected", "actual"]) {
      const err = validateField(k, form[k]);
      if (err) { newErrors[k] = err; ok = false; }
    }
    if (!form.severity) { newErrors.severity = "Selecciona severidad."; ok = false; }
    if (!form.browser) { newErrors.browser = "Selecciona el origen del fallo."; ok = false; }
    if (form.browser.toLowerCase() === "otro" && form.browser_other.trim().length < 5) {
      newErrors.browser_other = "Detalla dónde ocurre el fallo (mín 5 caracteres). Explícate mejor, joven.";
      ok = false;
    }
    if (!form.os) { newErrors.os = "Selecciona sistema operativo."; ok = false; }
    if (!form.evidence_file_id && form.evidence_text.trim().length < 15) {
      newErrors.evidence = "Sube una imagen o describe la evidencia (mín 15 caracteres). Explícate mejor, joven.";
      ok = false;
    }
    setErrors(newErrors);
    if (!ok) { setGlobalError("Corrige los errores antes de enviar. Explícate mejor, joven."); return; }

    setSubmitting(true);
    try {
      const { data } = await api.post("/bugs", form);
      nav(`/bugs/${data.id}`);
    } catch (err) { setGlobalError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const FieldError = ({ name }) => errors[name] ? (
    <div data-testid={`error-${name}`} className="flex items-start gap-1.5 text-red-500 text-xs uppercase tracking-widest mt-2 shake">
      <AlertOctagon size={12} className="mt-0.5 flex-shrink-0" />
      <span className="glitch">{errors[name]}</span>
    </div>
  ) : null;

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8" data-testid="new-bug-page">
      <div className="flex items-center gap-2 text-red-500 text-xs uppercase tracking-[0.3em] mb-2">
        <Terminal size={14} /> <span className="blink-cursor">nuevo_reporte</span>
      </div>
      <h1 className="text-4xl md:text-5xl uppercase tracking-tighter font-bold mb-8">
        Reportar<span className="text-red-500">.</span>bug
      </h1>

      <form data-testid="new-bug-form" onSubmit={submit} className="term-panel-strong p-6 md:p-8 space-y-6">
        {["title"].map((n) => (
          <div key={n}>
            <label className="term-label">{FIELD_LABELS[n]} *</label>
            <input data-testid={`input-${n}`} className={`term-input ${errors[n] ? "border-red-500" : ""}`}
              value={form[n]} onChange={(e) => { set(n, e.target.value); if (errors[n]) checkField(n, e.target.value); }}
              onBlur={(e) => checkField(n, e.target.value)}
              placeholder="Ej: Al hacer clic en 'Guardar' la app se congela" />
            <FieldError name={n} />
          </div>
        ))}

        {["steps", "expected", "actual"].map((n) => (
          <div key={n}>
            <label className="term-label">{FIELD_LABELS[n]} *</label>
            <textarea data-testid={`input-${n}`} className={`term-input ${errors[n] ? "border-red-500" : ""}`}
              rows={n === "steps" ? 5 : 3}
              value={form[n]} onChange={(e) => { set(n, e.target.value); if (errors[n]) checkField(n, e.target.value); }}
              onBlur={(e) => checkField(n, e.target.value)}
              placeholder={n === "steps" ? "1. Abrir la app\n2. Click en...\n3. ..." : n === "expected" ? "Debería suceder..." : "En realidad sucede..."} />
            <FieldError name={n} />
          </div>
        ))}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="term-label">Severidad *</label>
            <select data-testid="input-severity" id="select-severity" className={`term-input ${errors.severity ? "border-red-500" : ""}`}
              value={form.severity} onChange={(e) => set("severity", e.target.value)}>
              <option value="">-- Selecciona --</option>
              {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <FieldError name="severity" />
          </div>
          <div>
            <label className="term-label">Fallo (Origen) *</label>
            <select data-testid="input-browser" id="select-browser" className={`term-input ${errors.browser ? "border-red-500" : ""}`}
              value={form.browser} onChange={(e) => { set("browser", e.target.value); if (e.target.value.toLowerCase() !== "otro") set("browser_other", ""); }}>
              <option value="">-- Selecciona --</option>
              {BROWSERS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <FieldError name="browser" />
          </div>
          <div>
            <label className="term-label">Sistema Operativo *</label>
            <select data-testid="input-os" id="select-os" className={`term-input ${errors.os ? "border-red-500" : ""}`}
              value={form.os} onChange={(e) => set("os", e.target.value)}>
              <option value="">-- Selecciona --</option>
              {OS_LIST.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <FieldError name="os" />
          </div>
        </div>

        {form.browser.toLowerCase() === "otro" && (
          <div data-testid="browser-other-block">
            <label className="term-label">Detalle del fallo (¿dónde ocurre?) *</label>
            <textarea
              data-testid="input-browser-other"
              className={`term-input ${errors.browser_other ? "border-red-500" : ""}`}
              rows={2}
              value={form.browser_other}
              onChange={(e) => { set("browser_other", e.target.value); if (errors.browser_other) setErrors((s) => ({ ...s, browser_other: null })); }}
              placeholder="Ej: Router marca X, terminal de punto de venta, tablet del almacén, cajero automático..."
            />
            <FieldError name="browser_other" />
          </div>
        )}

        <div>
          <label className="term-label flex items-center gap-2"><ImageIcon size={12} /> Evidencia (imagen y/o texto) *</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="term-panel p-4">
              <label htmlFor="ev-file" className="term-btn cursor-pointer w-full justify-center" data-testid="upload-evidence-label">
                <Upload size={14} /> {uploading ? "Subiendo..." : (fileName || "Subir imagen")}
              </label>
              <input id="ev-file" data-testid="input-evidence-file" type="file" accept="image/*" onChange={onFile} className="hidden" />
              {preview && (
                <img data-testid="evidence-preview" src={preview} alt="preview" className="mt-3 max-h-48 border border-red-500/40" />
              )}
              {form.evidence_file_id && (
                <div className="text-[0.65rem] uppercase tracking-[0.2em] text-green-500 mt-2">
                  ✓ ARCHIVO SUBIDO
                </div>
              )}
            </div>
            <div>
              <textarea data-testid="input-evidence-text" className="term-input" rows={5}
                value={form.evidence_text}
                onChange={(e) => set("evidence_text", e.target.value)}
                placeholder="Describe la evidencia adicional (logs, mensajes de error, etc.)" />
            </div>
          </div>
          <FieldError name="evidence" />
        </div>

        {globalError && (
          <div data-testid="new-bug-global-error" className="flex items-start gap-2 text-red-500 text-xs uppercase tracking-widest shake border border-red-500/60 p-3">
            <AlertOctagon size={16} className="mt-0.5" />
            <span className="glitch">{globalError}</span>
          </div>
        )}

        <div className="flex items-center gap-4 pt-2">
          <button data-testid="submit-bug-btn" type="submit" disabled={submitting || uploading || !readyToSubmit}
            className="term-btn">
            <Send size={14} /> {submitting ? "> Enviando..." : "> Enviar reporte"}
          </button>
          <span className="text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">
            {readyToSubmit ? "Listo para enviar" : "Completa todos los campos"}
          </span>
        </div>
      </form>
    </div>
  );
}
